import { type ApolloError, useApolloClient } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useDispatch } from 'react-redux';

import type { AnalyticsActionOrigin } from 'src/modules/analytics/types';
import { useQuery, useToastMutation } from 'src/modules/apollo';
import { useAppConfig } from 'src/modules/appConfig';
import type { ActionName, Viewer } from 'src/types';
import * as Schema from 'src/types/graphql';

import { getLicences } from '../Licences/licences.graphql';
import type { GraphqlLicence } from '../Licences/types';
import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  removeLicence,
  removeLicenceFailure,
  removeLicenceSuccess,
  updateLicence,
  updateLicenceFailure,
  updateLicenceSuccess,
  updateVerifiedLicence,
  updateVerifiedLicenceFailure,
  updateVerifiedLicenceSuccess,
} from './actionCreators';
import {
  generateDeleteLicenceOptimisticResponse,
  generateUpdateLicenceOptimisticResponse,
  updateCacheDeleteLicence,
  updateCacheUpdateLicence,
  updateCacheUpdateVerifiedLicence,
} from './apolloCache';

export const useLicences = () => {
  const { language, zone } = useAppConfig();
  const { data, loading, error } = useQuery<
    Viewer<{ licences: GraphqlLicence[] }>
  >(getLicences, { variables: { languageCode: language, zone } });

  const licences = data?.viewer?.licences ?? [];

  return {
    licences,
    loading,
    error,
  };
};

const key = 'licences';

export const useUpdateLicence = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { language: languageCode, zone } = useAppConfig();

  const onMutate = () => {
    dispatch(updateLicence(action));
  };
  const onCompleted = (
    _: any,
    mutationMetadata?: {
      actionOrigin?: AnalyticsActionOrigin;
    },
  ) => {
    dispatch(updateLicenceSuccess(action, mutationMetadata));
  };
  const onError = (error: ApolloError) => {
    dispatch(updateLicenceFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.UpdateLicenceDocument, {
    key,
    errorActionRouteParameters: { to: 'licences', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage:
      action === 'ADD' ? t('Licence added') : t('Licence updated'),
    update: updateCacheUpdateLicence,
    optimisticResponse: generateUpdateLicenceOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
      languageCode,
      zone,
    }),
  });
};

export const useRemoveLicence = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();
  const { language: languageCode, zone } = useAppConfig();

  const onMutate = () => {
    dispatch(removeLicence());
  };
  const onCompleted = () => {
    dispatch(removeLicenceSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(removeLicenceFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.DeleteLicenceDocument, {
    key,
    errorActionRouteParameters: { to: 'licences', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Licence removed'),
    update: updateCacheDeleteLicence,
    optimisticResponse: generateDeleteLicenceOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
      languageCode,
      zone,
    }),
  });
};

export const useUpdateVerifiedLicence = () => {
  const dispatch = useDispatch();
  const { language: languageCode, zone } = useAppConfig();

  const onMutate = () => {
    dispatch(updateVerifiedLicence());
  };
  const onCompleted = () => {
    dispatch(updateVerifiedLicenceSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateVerifiedLicenceFailure(error));
  };

  return useToastMutation(Schema.UpdateVerifiedLicenceDocument, {
    key,
    errorActionRouteParameters: { to: 'licences', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: 'Verified licence updated',
    update: updateCacheUpdateVerifiedLicence(languageCode, zone),
  });
};
